import type { HierarcicalObjectReference, SearchPattern } from "@novorender/data-js-api";

import type { AppObjectDB } from "./appObjectDb";
import { SelectionSet } from "./selectionSet";

/** Extension-less federated sheets stamped by the drawings pipeline. */
const DRAWINGS_SOURCE_PATTERN: SearchPattern = {
    property: "Procore/DocumentSource",
    value: "drawings",
    exact: true,
};

/** Sheets whose path still carries a `.pdf` extension (Contains, not Equal). */
const PDF_PATH_PATTERN: SearchPattern = { property: "path", value: ".pdf" };

/**
 * PDF/drawing sheet ids for the current scene.
 *
 * Drawings are leaves (no descendants, no folder nesting in Procore), so two
 * scene-wide searches are enough: one on `Procore/DocumentSource`, one on path
 * for `.pdf`. They run in parallel and are merged; consumers never observe a
 * partial set.
 */
export async function discoverPdfObjectIds(db: AppObjectDB, abortSignal?: AbortSignal): Promise<SelectionSet> {
    abortSignal?.throwIfAborted();
    const [fromSource, fromPath] = await Promise.all([
        collectMatchingIds(db, DRAWINGS_SOURCE_PATTERN, abortSignal),
        collectMatchingIds(db, PDF_PATH_PATTERN, abortSignal),
    ]);
    abortSignal?.throwIfAborted();
    return fromSource.withAdded(fromPath);
}

async function collectMatchingIds(
    db: AppObjectDB,
    searchPattern: SearchPattern,
    abortSignal: AbortSignal | undefined,
): Promise<SelectionSet> {
    const ids = new Set<number>();
    const iterator = db.search({ searchPattern: [searchPattern] }, abortSignal);
    for await (const hit of iterator as AsyncIterable<HierarcicalObjectReference>) {
        abortSignal?.throwIfAborted();
        ids.add(hit.id);
    }
    return SelectionSet.fromIds(ids);
}
